/**
 * A campanha no portal: a cor da situação (P-11) e a LEITURA dos resultados publicados (§5.8 do plano do pacote
 * Campanhas + DV360; P-46 a P-50; DS-673 a DS-677). Sem React: é o que os testes unitários do portal exercitam.
 *
 * ## Um nível de detalhe, para os três públicos
 *
 * O interruptor da 5.2 decide QUEM vê (as empresas que recebem agora, pelas partes atuais do Pedido); os campos são os
 * mesmos para o Cliente, o Anunciante e o Parceiro comissionado:
 * impressões, cliques, CTR (da soma), "Resultados de … a …", "atualizado em", a situação dos dados e o rótulo fixo.
 * A lista "Nunca" do §5.8 (Media Cost, margem, comissão, CPM, CPC, % de entrega, meta, barra, nomes ou IDs do DV360,
 * links) não tem campo no contrato, e esta leitura só devolve os campos acima: o que a resposta trouxer a mais morre no
 * `parse` do contrato e, se passasse, não teria onde aparecer.
 *
 * ## A tela escreve o que o servidor manda
 *
 * A situação dos dados é DO SERVIDOR (a fonte da projeção e o portão vivo, DS-673 e DS-674): "Completo" com números,
 * "Em atualização" sem números (o conjunto de vínculos mudou ou deixou de estar vigente, a cobertura é parcial) e
 * "Ainda sem dados" (nenhuma Campaign vinculada). A tela não recalcula nada disso, nem a CTR, que vem arredondada UMA
 * vez pelo domínio.
 *
 * ## Falha fechada
 *
 * "Completo" sem os campos que o definem é lido como "Em atualização": a tela nunca inventa um número nem escreve um
 * zero que o servidor não afirmou. Os campos são os quatro do CHECK da projeção (0200, `num_nonnulls(impressoes,
 * cliques, resultados_ate, atualizado_em) = 4`): impressões, cliques, o "até X" e o instante da atualização, legível.
 * Número sem a data da atualização não sai, porque o "atualizado em" é parte do conteúdo (§5.8, P-47).
 *
 * ## Nada guardado
 *
 * Os números vivem só no estado da consulta. Revogar vale na próxima consulta (o portão é vivo, §6 C3), e nada no
 * armazenamento do navegador os traria de volta: este módulo não grava, e o portal não tem cache.
 */
import { z } from 'zod'
import { campanhaDoParceiroNoPortal, type ResultadosDaCampanhaNoPortal } from '@dv/contracts'
import { dataBr } from './formato.js'

/** O bloco "Campanhas" do Portal do Parceiro (P-48): o cartão sem o valor, validado pelo contrato de produção. */
export const listaDeCampanhasDoParceiro = z.array(campanhaDoParceiroNoPortal)

/*
  P-11 (D-189, DS-658): o selo diz o que a DigitalView registrou, pela `situacao` da campanha ("Ainda não começou",
  "Em andamento", "Pausada pela DigitalView", "Concluída pela DigitalView"). A coluna antiga `status` fica no vocabulário
  da versão anterior (a Pausada é 'Veiculando' nela) e só vale quando a linha ainda não tem a situação: a linha
  materializada antes da coluna nova, até a próxima materialização. As cores seguem as do selo antigo: o verde do que
  está no ar, e o slate do informativo (a pausa e a conclusão são registro da DigitalView, sem ação de quem lê). O
  cartão do Cliente e o do Parceiro leem a MESMA tabela.
*/
export const COR_DA_CAMPANHA: Readonly<Record<string, string>> = {
  'Em andamento': 'verde',
  'Ainda não começou': 'slate',
  'Pausada pela DigitalView': 'slate',
  'Concluída pela DigitalView': 'slate',
  Veiculando: 'verde',
  Programada: 'slate',
  Concluída: 'slate',
}

/** O fuso do negócio (o do `fn_hoje()` e da agenda do DV360): o "atualizado em" não muda com o relógio de quem lê. */
export const FUSO_DO_NEGOCIO = 'America/Sao_Paulo'

/** O que o bloco de resultados mostra, já em texto de tela. Nenhum outro campo existe aqui. */
export type LeituraDosResultados =
  | {
      readonly tipo: 'numeros'
      readonly impressoes: string
      readonly cliques: string
      /** "0,53%"; sem impressões, a CTR não existe e a tela escreve "—". */
      readonly ctr: string
      /**
       * "Resultados de 02/05/2026 a 04/06/2026", ou, sem o primeiro dia, "Sem veiculação registrada até 04/06/2026" (o
       * termo do GLOSSARIO_UI para o dia sem fato, com o "até X"; nunca uma frase sobre a campanha inteira, que pode ter
       * outras mídias).
       */
      readonly periodo: string
      /** "atualizado em 05/06/2026 09:31", no fuso do negócio. Sem ele, não há números (falha fechada). */
      readonly atualizadoEm: string
      /** "Completo até 04/06" (§5.8: "Completo até dd/mm"). */
      readonly situacao: string
      readonly rotulo: string
    }
  | {
      readonly tipo: 'sem-numeros'
      readonly situacao: 'Em atualização' | 'Ainda sem dados'
      readonly rotulo: string
    }

const MARCADOR_VAZIO = '—'

const agrupar = (inteiro: string): string => inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

/** "2345678" vira "2.345.678". Contagem é texto do começo ao fim: 19 dígitos não passam por `Number`. */
export function contagemBr(contagem: string): string {
  return agrupar(contagem.replace(/^0+(?=\d)/, ''))
}

/** "0.53" vira "0,53%"; null vira "—". A casa decimal é a que o domínio arredondou, sem segundo arredondamento. */
export function ctrBr(ctr: string | null): string {
  if (ctr === null) return MARCADOR_VAZIO
  const [inteiro = '0', decimal = ''] = ctr.split('.')
  return `${agrupar(inteiro.replace(/^0+(?=\d)/, ''))}${decimal === '' ? '' : `,${decimal}`}%`
}

/** `2026-06-04` vira `04/06`, o "dd/mm" do "Completo até". Recorte de string: data nominal não tem fuso. */
export function dataCurtaBr(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`
}

const formatoDoInstante = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_DO_NEGOCIO,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/**
 * O instante ISO 8601 no fuso do negócio: "05/06/2026 09:31", como a prévia do cartão na 5.2 o escreve. As partes vêm
 * do `formatToParts`, e não da string pronta do `Intl`, cuja pontuação muda entre versões do ICU. Instante ilegível
 * vira null, e a leitura dos resultados vira "Em atualização" em vez de escrever "Invalid Date" ou números sem data.
 */
export function instanteBr(iso: string): string | null {
  const instante = new Date(iso)
  if (Number.isNaN(instante.getTime())) return null
  const parte = (tipo: Intl.DateTimeFormatPartTypes): string =>
    formatoDoInstante.formatToParts(instante).find((p) => p.type === tipo)?.value ?? ''
  return `${parte('day')}/${parte('month')}/${parte('year')} ${parte('hour')}:${parte('minute')}`
}

/** Os resultados como a tela os mostra: só os campos do §5.8, e a situação dos dados como o servidor a mandou. */
export function lerResultados(resultados: ResultadosDaCampanhaNoPortal): LeituraDosResultados {
  const { impressoes, cliques, resultadosAte } = resultados
  const atualizado = resultados.atualizadoEm === null ? null : instanteBr(resultados.atualizadoEm)
  if (
    resultados.situacaoDosDados === 'Completo'
    && impressoes !== null
    && cliques !== null
    && resultadosAte !== null
    && atualizado !== null
  ) {
    return {
      tipo: 'numeros',
      impressoes: contagemBr(impressoes),
      cliques: contagemBr(cliques),
      ctr: ctrBr(resultados.ctrPercentual),
      periodo: resultados.resultadosDe === null
        ? `Sem veiculação registrada até ${dataBr(resultadosAte)}`
        : `Resultados de ${dataBr(resultados.resultadosDe)} a ${dataBr(resultadosAte)}`,
      atualizadoEm: `atualizado em ${atualizado}`,
      situacao: `Completo até ${dataCurtaBr(resultadosAte)}`,
      rotulo: resultados.rotulo,
    }
  }
  return {
    tipo: 'sem-numeros',
    situacao: resultados.situacaoDosDados === 'Ainda sem dados' ? 'Ainda sem dados' : 'Em atualização',
    rotulo: resultados.rotulo,
  }
}

/**
 * O aviso da campanha Programada no cartão do Cliente e do Anunciante (P-11; DS-658 (o); DS-677 (g)).
 *
 * "Ainda não começou a veicular" diz o que a DigitalView registrou: o status local. O DV360 pode ter veiculado antes do
 * início planejado, ou antes de a DigitalView registrar o início, e o status local nunca esconde o gasto (D-191). Com o
 * primeiro dia de veiculação publicado, a frase vira o fato que vale nos dois casos: o início planejado.
 */
export function avisoDaProgramada(inicio: string, resultados: ResultadosDaCampanhaNoPortal | null | undefined): string {
  const veiculou = resultados !== undefined && resultados !== null && resultados.resultadosDe !== null
  return veiculou ? `Início planejado em ${dataBr(inicio)}` : `Ainda não começou a veicular, início em ${dataBr(inicio)}`
}
